import React from 'react';
import ReactDOM from 'react-dom';
import ReactHintFactory from 'react-hint';
import { useReduxState } from 'hooks';
import { AchievementUserStatus } from 'components/achievements';
import { Badge, ProductLinkWithIcon } from 'components/common';
import { CourseProgressStatus } from 'components/common/tasks';

import type {
  AchievementProgress,
  CourseProgress,
  PathCompletion,
  PathStepProgress,
  ResolvedItemInPathStep
} from 'shared-types';
import type { BadgeCategory } from 'components/common/Badge';

const ReactHint = ReactHintFactory(React);

interface Props {
  iconOnly?: boolean;
  id: string;
  items?: ResolvedItemInPathStep[];
  pathCompletion: Pick<
    PathCompletion,
    'id' | 'completionDate' | 'completedCourses'
  > | null;
  progressData: {
    completionDate: string;
    completed: number;
    expired: number;
    started: number;
  };
  progressDataByItem?: PathStepProgress['steps'][0]['itemProgressByTypeId'];
}

const PathStepProgressStatus = ({
  iconOnly,
  id,
  items,
  pathCompletion,
  progressData,
  progressDataByItem
}: Props) => {
  const { messages } = useReduxState();

  if (!progressData) return null;

  const renderTooltip = () => {
    return (
      <div className="vuo-tooltip__content">
        {items?.map((item) => {
          const itemProgress = progressDataByItem[`${item.type}${item.id}`];

          return (
            <div
              key={item.id}
              style={{
                alignItems: 'center',
                display: 'flex',
                gap: 20,
                justifyContent: 'space-between',
                padding: '10px 5px'
              }}
            >
              <div className="item-title">
                <ProductLinkWithIcon
                  disableLink={true}
                  titleStyle={{ fontWeight: 600 }}
                  {...(item.type === 'achievement'
                    ? { achievement: item.achievement }
                    : item.type === 'course'
                    ? { course: item.course }
                    : item.type === 'event'
                    ? { calendarEvent: item.event }
                    : {})}
                />
              </div>
              <div className="item-status">
                {!itemProgress ? (
                  <Badge isRounded={false} text={messages.commonNotStarted} />
                ) : item.type === 'achievement' ? (
                  <AchievementUserStatus
                    achievementData={itemProgress as AchievementProgress}
                    achievementType={(itemProgress as AchievementProgress).type}
                    numberOfItems={
                      (itemProgress as AchievementProgress)
                        ?.numberOfTemplateItems
                    }
                  />
                ) : item.type === 'course' ? (
                  <CourseProgressStatus
                    course={item.course}
                    id={`item-progress-${item.type}-${item.id}`}
                    progressData={itemProgress as CourseProgress}
                  />
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div>
      {items && progressDataByItem
        ? ReactDOM.createPortal(
            <ReactHint
              attribute={`data-path-step-progress-status-${id}`}
              autoPosition={true}
              className="vuo-tooltip-alt"
              events={true}
              delay={50}
              onRenderContent={renderTooltip}
            />,
            document.querySelector('.dynamic-tooltip-root')
          )
        : null}

      <Badge
        isRounded={false}
        {...(iconOnly
          ? {
              style: {
                borderRadius: 24,
                justifyContent: 'center',
                paddingLeft: 0,
                paddingRight: 0,
                height: 24,
                width: 24
              }
            }
          : {})}
        {...{ [`data-path-step-progress-status-${id}`]: true }}
        {...(progressData?.completed > 0
          ? {
              category: 'success-light' as BadgeCategory,
              icon: 'check',
              text: iconOnly ? null : messages.commonCompleted,
              tooltip: !items ? messages.commonCompleted : null
            }
          : progressData?.expired > 0 && pathCompletion
          ? {
              category: 'error-light' as BadgeCategory,
              icon: 'check',
              text: iconOnly ? null : messages.commonExpired,
              tooltip: !items ? messages.commonExpired : null
            }
          : progressData?.started > 0
          ? {
              icon: 'hourglass-start',
              text: iconOnly ? null : messages.commonInProgress,
              tooltip: !items ? messages.commonInProgress : null
            }
          : {
              icon: iconOnly ? 'minus' : null,
              text: iconOnly ? null : messages.commonNotStarted,
              ...(iconOnly && !items ? { style: { display: 'none' } } : {})
            })}
      />
    </div>
  );
};

export default PathStepProgressStatus;
